/**
 * <topcoat-calc> — coverage calculator for Titan Shield Gloss and SEMCO Sealer MATTE.
 *
 * Entirely separate from the Liquid Membrane calculator (#slmCalc / slm-coverage-calculator.liquid),
 * which is generated outside the theme and re-uploaded regularly. Nothing here reads, writes or
 * depends on that file, so exports of it can never break this and vice versa.
 *
 * Coverage rate and coat count come from section settings:
 *   Titan Shield Gloss   150 sq ft per gallon at 3 coats
 *   SEMCO Sealer MATTE   120 sq ft per gallon at 2 coats
 * Both are low-end figures, so the calculator errs toward buying enough.
 *
 * Two differences from Liquid Membrane that this handles explicitly:
 *   - variant options are Size/Color here, Color/Size there, so the size option is found by
 *     matching values against the ladder rather than by position
 *   - Titan Shield labels its largest size "5 Gallons" (plural) where others use "5 Gallon"
 *
 * Prep rules follow prep-rules.md (Surface Preparation Guide, confirmed Aug 2026). They are
 * surface-based, so they are the same regardless of which coating is going on top.
 */
(function () {
  var SIZES = [
    { match: /5\s*[- ]?\s*gal/i, gal: 5, label: '5 Gallon' },
    { match: /gal/i, gal: 1, label: '1 Gallon' },
    { match: /quart|\bqt\b/i, gal: 0.25, label: '1 Quart', smallOnly: true },
    { match: /pint|\bpt\b/i, gal: 0.125, label: '1 Pint', smallOnly: true }
  ];

  var CLEAN = {
    nulift: { name: 'Nu-Lift Cleaner', sizes: [
      { t: '1 Quart', cov: 125, price: 23.65, id: 37422119682201 },
      { t: '1 Gallon', cov: 500, price: 47.30, id: 37422119714969 },
      { t: '5 Gallon', cov: 2500, price: 216.10, id: 44722162761962 }] },
    stone: { name: 'Stone Soap', sizes: [
      { t: '1 Quart', cov: 250, price: 27.10, id: 37422171422873 },
      { t: '1 Gallon', cov: 1000, price: 54.30, id: 37422171455641 },
      { t: '5 Gallon', cov: 5000, price: 247.55, id: 44722171904234 }] },
    power: { name: 'Power Cleaner', sizes: [
      { t: '1 Quart', cov: 250, price: 27.30, id: 37422155137177 },
      { t: '1 Gallon', cov: 1000, price: 54.50, id: 37422155169945 },
      { t: '5 Gallon', cov: 5000, price: 249.90, id: 44722165678314 }] }
  };

  var PREP = {
    A: { items: ['stone'], text: 'New concrete only needs Stone Soap to clear dirt and grime.' },
    B: { items: ['power', 'stone'], text: 'Power Cleaner cuts through grease, weathering and build-up, then Stone Soap clears the residue.' },
    C: { items: ['power', 'nulift'], text: 'An unknown or non water-based coating needs the heavy-duty pair — Power Cleaner then Nu-Lift.' },
    D: { items: ['nulift', 'stone'], text: 'Nu-Lift lifts mineral deposits, efflorescence and oxidation, then Stone Soap clears the residue.' },
    E: { items: [], text: 'Wood needs no cleaner — sand with 60-grit for adhesion. Avoid pressure washing; wood absorbs water.' },
    NONE: { items: [], text: 'Already coated with SEMCO — no cleaner needed. Just make sure the surface is clean, dry and free of dust.' }
  };

  var SURFACES = [
    { v: 'semco', t: 'Already coated with SEMCO Liquid Membrane', prep: 'NONE' },
    { v: 'new-concrete', t: 'New concrete (under about 1 month)', prep: 'A' },
    { v: 'old-concrete', t: 'Older or weathered concrete', prep: 'D' },
    { v: 'oily-concrete', t: 'Concrete with oil or grease', prep: 'B' },
    { v: 'basement', t: 'Basement walls or floor', prep: 'D' },
    { v: 'metal', t: 'Metal (roof, gutters, RV, trailer)', prep: 'D' },
    { v: 'wood', t: 'Wood or plywood', prep: 'E' },
    { v: 'coated', t: 'Previously coated, chemistry unknown', prep: 'C' }
  ];

  var MAX_PACKS = 12;

  function money(n) { return '$' + n.toFixed(2); }
  function num(n) { return Number(n).toLocaleString('en-US'); }
  function sizeOf(t) {
    for (var i = 0; i < SIZES.length; i++) if (SIZES[i].match.test(t)) return SIZES[i];
    return null;
  }

  function TopcoatCalc() { return Reflect.construct(HTMLElement, [], TopcoatCalc); }
  TopcoatCalc.prototype = Object.create(HTMLElement.prototype);
  TopcoatCalc.prototype.constructor = TopcoatCalc;
  Object.setPrototypeOf(TopcoatCalc, HTMLElement);

  TopcoatCalc.prototype.connectedCallback = function () {
    var cfg = this.querySelector('[data-tc-config]');
    var vars = this.querySelector('[data-tc-variants]');
    if (!cfg || !vars) return;

    try {
      this.cfg = JSON.parse(cfg.textContent);
      this.variants = JSON.parse(vars.textContent) || [];
    } catch (e) {
      console.warn('[topcoat-calc] bad data', e);
      this.hidden = true;
      return;
    }
    if (!this.variants.length) { this.hidden = true; return; }

    var self = this;
    this.el = {};
    ['surface', 'area', 'go', 'prep', 'empty', 'result', 'lines', 'total', 'add', 'note', 'color', 'colorwrap']
      .forEach(function (k) { self.el[k] = self.querySelector('[data-tc-' + k + ']'); });

    this.lines = [];
    this.detectOptions();
    this.fillSurfaces();
    this.fillColors();

    if (this.el.surface) this.el.surface.addEventListener('change', this.onSurface.bind(this));
    if (this.el.go) this.el.go.addEventListener('click', this.render.bind(this));
    if (this.el.color) this.el.color.addEventListener('change', this.render.bind(this));
    if (this.el.area) this.el.area.addEventListener('keyup', function (e) { if (e.key === 'Enter') self.render(); });
    if (this.el.add) this.el.add.addEventListener('click', this.addToCart.bind(this));
  };

  /** Find the size option by content, not position — it is option1 here, option2 on other products. */
  TopcoatCalc.prototype.detectOptions = function () {
    var hits = [0, 0, 0];
    this.variants.forEach(function (v) {
      ['option1', 'option2', 'option3'].forEach(function (k, i) { if (v[k] && sizeOf(v[k])) hits[i]++; });
    });
    var best = hits.indexOf(Math.max.apply(null, hits));
    this.sizeKey = ['option1', 'option2', 'option3'][best];
    this.colorKey = this.sizeKey === 'option1' ? 'option2' : 'option1';
  };

  TopcoatCalc.prototype.fillSurfaces = function () {
    if (!this.el.surface) return;
    this.el.surface.innerHTML = '<option value="">Choose…</option>' +
      SURFACES.map(function (s) { return '<option value="' + s.v + '">' + s.t + '</option>'; }).join('');
  };

  TopcoatCalc.prototype.fillColors = function () {
    if (!this.el.color) return;
    var seen = [], self = this;
    this.variants.forEach(function (v) {
      var c = v[self.colorKey];
      if (c && seen.indexOf(c) === -1) seen.push(c);
    });
    if (seen.length < 2) { if (this.el.colorwrap) this.el.colorwrap.hidden = true; return; }
    this.el.color.innerHTML = seen.map(function (c) { return '<option>' + c + '</option>'; }).join('');
  };

  TopcoatCalc.prototype.surface = function () {
    var v = this.el.surface && this.el.surface.value;
    for (var i = 0; i < SURFACES.length; i++) if (SURFACES[i].v === v) return SURFACES[i];
    return null;
  };

  TopcoatCalc.prototype.onSurface = function () {
    var s = this.surface();
    if (this.el.prep) {
      if (!s) { this.el.prep.hidden = true; }
      else {
        this.el.prep.innerHTML = PREP[s.prep].text + (s.note ? ' <strong>' + s.note + '</strong>' : '');
        this.el.prep.hidden = false;
      }
    }
    this.render();
  };

  TopcoatCalc.prototype.findVariant = function (size, color) {
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i], sv = v[this.sizeKey];
      if (!sv) continue;
      var s = sizeOf(sv);
      if (!s || s.label !== size.label) continue;
      if (color && v[this.colorKey] && v[this.colorKey] !== color) continue;
      return v;
    }
    return null;
  };

  /** Cheapest containers that cover the area; small sizes only when they alone are enough. */
  TopcoatCalc.prototype.fit = function (area) {
    var color = (this.el.color && this.el.colorwrap && !this.el.colorwrap.hidden) ? this.el.color.value : null;
    var per = Number(this.cfg.coverage) || 0;
    if (!per) return null;
    var best = null, fallback = null, self = this;

    SIZES.forEach(function (s) {
      var cov = per * s.gal;
      if (s.smallOnly && area > cov) return;
      var v = self.findVariant(s, color);
      if (!v) return;
      var price = v.price / 100, packs = Math.ceil(area / cov), cost = packs * price;
      var cand = { label: s.label, packs: packs, price: price, cost: cost, cov: cov, id: v.id };
      if (!fallback || cost < fallback.cost - 0.001) fallback = cand;
      if (packs > MAX_PACKS) return;
      if (!best || cost < best.cost - 0.001) best = cand;
    });
    return best || fallback;
  };

  TopcoatCalc.prototype.fitCleaner = function (sizes, area) {
    var best = null;
    sizes.forEach(function (s) {
      var packs = Math.ceil(area / s.cov), cost = packs * s.price;
      if (!best || cost < best.cost - 0.001) best = { size: s, packs: packs, cost: cost, cov: s.cov };
    });
    return best;
  };

  TopcoatCalc.prototype.build = function () {
    var s = this.surface();
    var area = parseFloat(this.el.area && this.el.area.value);
    if (!s || !isFinite(area) || area <= 0) return false;

    var main = this.fit(area);
    if (!main) return false;

    this.lines = [{
      id: main.id, qty: main.packs, price: main.price, on: true, lock: true,
      t: main.packs + ' × ' + main.label + ' ' + this.cfg.productTitle,
      d: 'Covers up to ' + num(Math.round(main.cov)) + ' sq ft each, at ' + this.cfg.coats + ' coats'
    }];

    var self = this;
    (PREP[s.prep].items || []).forEach(function (k) {
      var c = CLEAN[k], fit = self.fitCleaner(c.sizes, area);
      if (!fit) return;
      self.lines.push({
        id: fit.size.id, qty: fit.packs, price: fit.size.price, on: true,
        t: fit.packs + ' × ' + fit.size.t + ' ' + c.name,
        d: 'Covers up to ' + num(fit.cov) + ' sq ft each'
      });
    });
    return true;
  };

  TopcoatCalc.prototype.render = function () {
    if (!this.build()) {
      if (this.el.result) this.el.result.hidden = true;
      if (this.el.empty) this.el.empty.hidden = false;
      return;
    }
    var self = this;
    if (this.el.lines) {
      this.el.lines.innerHTML = this.lines.map(function (l, i) {
        return '<label class="topcoat-calc__line">' +
          '<input type="checkbox" data-i="' + i + '"' + (l.on ? ' checked' : '') + (l.lock ? ' disabled' : '') + '>' +
          '<span class="topcoat-calc__line-t">' + l.t + '<span class="topcoat-calc__line-d">' + l.d + '</span></span>' +
          '<span class="topcoat-calc__line-p">' + money(l.qty * l.price) + '</span></label>';
      }).join('');
      Array.prototype.forEach.call(this.el.lines.querySelectorAll('input[data-i]'), function (cb) {
        cb.addEventListener('change', function () { self.lines[+this.dataset.i].on = this.checked; self.total(); });
      });
    }
    this.total();
    if (this.el.empty) this.el.empty.hidden = true;
    if (this.el.result) this.el.result.hidden = false;
  };

  TopcoatCalc.prototype.total = function () {
    if (!this.el.total) return;
    var t = this.lines.reduce(function (a, l) { return a + (l.on ? l.qty * l.price : 0); }, 0);
    this.el.total.textContent = money(t);
  };

  TopcoatCalc.prototype.note = function (m, err) {
    if (!this.el.note) return;
    this.el.note.textContent = m || '';
    if (err) this.el.note.setAttribute('data-err', '1'); else this.el.note.removeAttribute('data-err');
  };

  TopcoatCalc.prototype.addToCart = function () {
    var items = this.lines.filter(function (l) { return l.on; })
      .map(function (l) { return { id: l.id, quantity: l.qty }; });
    if (!items.length) { this.note('Nothing selected.', true); return; }
    var self = this;
    this.el.add.disabled = true;
    this.note('Adding to cart…');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw new Error(d.description || d.message || 'Could not add to cart.'); return d; }); })
      .then(function () { window.location = '/cart'; })
      .catch(function (e) { self.el.add.disabled = false; self.note(e.message, true); });
  };

  if (!window.customElements.get('topcoat-calc')) window.customElements.define('topcoat-calc', TopcoatCalc);
})();
